// Aba "Frases gatilho" do Flowbuilder (/phrase-lists) — mockup "Frases A":
// abas + busca + "Nova frase gatilho", caixa de ajuda Exata/Parcial e
// tabela Nome · Frases · Fluxo · Conexões · Disparos 7d · Status · Ações.
// "Disparos 7d" ainda não tem contagem no backend: exibe "—" com tooltip.
import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { TextFields, AccountTree } from "@mui/icons-material";

import MainContainer from "../../components/MainContainer";
import FlowBuilderNav from "../../components/FlowBuilderNav";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import CampaignModalPhrase from "../../components/CampaignModalPhrase";
import { Can } from "../../components/Can";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { AEROS } from "../../styles/aerosTokens";
import useFlowPageStyles from "../FlowBuilder/flowPageStyles";
import { matchesPhraseSearch } from "../FlowBuilderConfig/flowRules";

const COLS = "1.6fr 2fr 1.3fr 1.3fr 0.9fr 0.9fr 90px";

const CampaignsPhrase = () => {
  const classes = useFlowPageStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [flows, setFlows] = useState([]);
  const [whatsApps, setWhatsApps] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [deleting, setDeleting] = useState(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, f, w] = await Promise.all([api.get("/flowcampaign"), api.get("/flowbuilder"), api.get("/whatsapp")]);
      setRows(Array.isArray(c.data?.flow) ? c.data.flow : []);
      setFlows(f.data?.flows || []);
      setWhatsApps(Array.isArray(w.data) ? w.data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const flowName = (id) => flows.find((f) => f.id === id)?.name;
  const visible = useMemo(
    () => rows.filter((r) => matchesPhraseSearch(r, flowName(r.flowId), search)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, flows, search]
  );

  const handleDelete = async () => {
    if (!deleting) return;
    setDeletingBusy(true);
    try {
      await api.delete(`/flowcampaign/${deleting.id}`);
      toast.success(i18n.t("aeros.flow.phrases.deleted"));
      setDeleting(null);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingBusy(false);
    }
  };

  const openNew = () => {
    setSelectedId(undefined);
    setModalOpen(true);
  };

  return (
    <MainContainer>
      <AerosConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        icon={<DeleteOutlineIcon />}
        title={i18n.t("aeros.flow.phrases.deleteTitle", { name: deleting?.name || "" })}
        description={i18n.t("aeros.flow.phrases.deleteText")}
        confirmLabel={i18n.t("aeros.flow.phrases.deleteConfirm")}
        cancelLabel={i18n.t("aeros.flow.cancel")}
        loading={deletingBusy}
      />
      <CampaignModalPhrase
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        FlowCampaignId={selectedId}
        campaigns={rows}
        onSave={load}
      />

      <div className={classes.pageHeader}>
        <FlowBuilderNav counts={{ phrases: rows.length }} />
        <div className={classes.headerActions}>
          <InputBase
            className={classes.searchPill}
            placeholder={i18n.t("aeros.flow.phrases.search")}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("aeros.flow.phrases.search") }}
          />
          <Button variant="contained" color="primary" className={classes.gradientButton} startIcon={<AddIcon />} onClick={openNew}>
            {i18n.t("aeros.flow.phrases.new")}
          </Button>
        </div>
      </div>

      <div className={classes.pageBody}>
        <div className={classes.infoBox}>
          <InfoOutlinedIcon />
          <div>
            {i18n.t("aeros.flow.phrases.info")} <b>{i18n.t("aeros.flow.phrases.exact")}</b>{" "}
            {i18n.t("aeros.flow.phrases.infoExact")} <b>{i18n.t("aeros.flow.phrases.partial")}</b>{" "}
            {i18n.t("aeros.flow.phrases.infoPartial")}
          </div>
        </div>

        {loading ? (
          <div className={classes.loading}>
            <CircularProgress color="primary" />
          </div>
        ) : visible.length === 0 ? (
          <div className={classes.emptyState}>
            <TextFields className={classes.emptyIcon} />
            <div className={classes.emptyTitle}>
              {i18n.t(search ? "aeros.flow.phrases.emptySearch" : "aeros.flow.phrases.empty")}
            </div>
            {!search && <div className={classes.emptyDescription}>{i18n.t("aeros.flow.phrases.emptyHint")}</div>}
          </div>
        ) : (
          <div className={classes.table}>
            <div className={classes.tableScroll}>
              <div className={`${classes.row} ${classes.head}`} style={{ gridTemplateColumns: COLS }} role="row">
                <div className={`${classes.cell} ${classes.cellFirst}`}>{i18n.t("aeros.flow.phrases.col.name")}</div>
                <div className={classes.cell}>{i18n.t("aeros.flow.phrases.col.phrases")}</div>
                <div className={classes.cell}>{i18n.t("aeros.flow.phrases.col.flow")}</div>
                <div className={classes.cell}>{i18n.t("aeros.flow.phrases.col.connections")}</div>
                <div className={classes.cell}>{i18n.t("aeros.flow.phrases.col.hits")}</div>
                <div className={classes.cell}>{i18n.t("aeros.flow.phrases.col.status")}</div>
                <div className={`${classes.cell} ${classes.cellLast}`}>{i18n.t("aeros.flow.phrases.col.actions")}</div>
              </div>
              {visible.map((row) => {
                const phrases = Array.isArray(row.phrase) ? row.phrase : [];
                const conns = Array.isArray(row.whatsappIds) ? row.whatsappIds : [];
                const fname = flowName(row.flowId);
                const active = row.status !== false;
                return (
                  <div key={row.id} className={classes.row} style={{ gridTemplateColumns: COLS }} role="row">
                    <div className={`${classes.cell} ${classes.cellFirst} ${classes.nameCell}`}>
                      <div className={classes.tile}><TextFields /></div>
                      <div style={{ minWidth: 0 }}>
                        <div className={classes.name}>{row.name}</div>
                        <div className={classes.sub}>{i18n.t("aeros.flow.phrases.count", { count: phrases.length })}</div>
                      </div>
                    </div>
                    <div className={`${classes.cell} ${classes.chips}`}>
                      {phrases.map((p, i) => {
                        const partial = p?.type === "partial";
                        return (
                          <span key={i} className={`${classes.phraseChip} ${partial ? classes.chipPartial : classes.chipExact}`}>
                            {p?.text ?? String(p)}
                            <small>{i18n.t(partial ? "aeros.flow.phrases.partialShort" : "aeros.flow.phrases.exactShort")}</small>
                          </span>
                        );
                      })}
                    </div>
                    <div className={`${classes.cell} ${classes.flowCell}`}>
                      <AccountTree className={classes.flowIcon} />
                      <span className={`${classes.ellipsis} ${fname ? "" : classes.muted}`}>{fname || i18n.t("aeros.flow.phrases.noFlow")}</span>
                    </div>
                    <div className={`${classes.cell} ${classes.chips}`} style={{ gap: 4 }}>
                      {conns.length === 0 && <span className={classes.muted} style={{ fontSize: 12 }}>{i18n.t("aeros.flow.phrases.noConnection")}</span>}
                      {conns.map((id) => {
                        const w = whatsApps.find((x) => x.id === id);
                        return (
                          <span key={id} className={classes.connChip}>
                            <span className={classes.dot} style={{ background: w?.status === "CONNECTED" ? AEROS.success : AEROS.error }} />
                            {w?.name || `#${id}`}
                          </span>
                        );
                      })}
                    </div>
                    <div className={`${classes.cell} ${classes.hits}`}>
                      <Tooltip title={i18n.t("aeros.flow.phrases.hitsUnavailable")}><span>—</span></Tooltip>
                    </div>
                    <div className={classes.cell}>
                      <span className={`${classes.statusPill} ${active ? classes.pillActive : classes.pillInactive}`}>
                        <span className={classes.dot} />
                        {i18n.t(active ? "aeros.flow.phrases.active" : "aeros.flow.phrases.inactive")}
                      </span>
                    </div>
                    <div className={`${classes.cell} ${classes.cellLast}`}>
                      <IconButton
                        className={classes.actionButton}
                        aria-label={i18n.t("aeros.flow.edit")}
                        onClick={() => {
                          setSelectedId(row.id);
                          setModalOpen(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <Can
                        role={user.profile}
                        perform="contacts-page:deleteContact"
                        yes={() => (
                          <IconButton
                            className={`${classes.actionButton} ${classes.deleteButton}`}
                            aria-label={i18n.t("aeros.flow.delete")}
                            onClick={() => setDeleting(row)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        )}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default CampaignsPhrase;
